// Pure rendering rules for the native Home preview. Every function here mirrors a
// specific rule in the iOS client (`apps/ios/Sources` in wager-mobile-platform) so
// the Studio canvas draws the same thing the simulator draws for the same manifest
// and fixture catalog. Keep the Swift reference next to each port.
import previewCatalogBundle from '../../../../contracts/native-sportsbook/v1/preview-catalog.json'
import previewRealtimeBundle from '../../../../contracts/native-sportsbook/v1/preview-realtime.json'

const NON_HEX_CHARACTERS_PATTERN = /[^0-9a-f]/gi
const NON_SIGNED_DIGIT_PATTERN = /[^0-9-]/g

export const ALL_SPORTS_ID = 'all'

// SportsbookSportIdentity (Models.swift)
const LEAGUE_SCOPED_IDS = new Set(['CFL', 'MLB', 'MLS', 'NBA', 'NCAAB', 'NCAAF', 'NFL', 'NHL', 'UFC', 'WNBA'])
const SPORT_ORDER = ['AMERICAN_FOOTBALL', 'FOOTBALL', 'BASKETBALL', 'BASEBALL', 'ICE_HOCKEY', 'TENNIS', 'MMA', 'RUGBY']

export function normalizedProviderID(value) {
  return String(value ?? '').trim().toUpperCase().replaceAll('-', '_').replaceAll(' ', '_')
}

export function canonicalSportID(sport) {
  const identifier = normalizedProviderID(sport)
  switch (identifier) {
    case 'FOOTBALL': case 'SOCCER': case 'MLS': return 'FOOTBALL'
    case 'AMERICAN_FOOTBALL': case 'CFL': case 'NCAAF': case 'NFL': return 'AMERICAN_FOOTBALL'
    case 'BASEBALL': case 'MLB': return 'BASEBALL'
    case 'BASKETBALL': case 'NBA': case 'NCAAB': case 'WNBA': return 'BASKETBALL'
    case 'HOCKEY': case 'ICE_HOCKEY': case 'NHL': return 'ICE_HOCKEY'
    case 'MMA': case 'UFC': return 'MMA'
    case 'RUGBY': case 'RUGBY_LEAGUE': case 'RUGBY_UNION': return 'RUGBY'
    case 'TENNIS': return 'TENNIS'
    default: return identifier
  }
}

export function sportDisplayName(identifier) {
  switch (canonicalSportID(identifier)) {
    case 'FOOTBALL': return 'Soccer'
    case 'AMERICAN_FOOTBALL': return 'American Football'
    case 'BASEBALL': return 'Baseball'
    case 'BASKETBALL': return 'Basketball'
    case 'ICE_HOCKEY': return 'Ice Hockey'
    case 'MMA': return 'MMA'
    case 'RUGBY': return 'Rugby'
    case 'TENNIS': return 'Tennis'
    default:
      return canonicalSportID(identifier).split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')
  }
}

// SF Symbols have no web equivalent; these glyphs stand in for `symbol(for:)`.
export function sportGlyph(identifier) {
  switch (canonicalSportID(identifier)) {
    case 'FOOTBALL': return '⚽'
    case 'AMERICAN_FOOTBALL': return '🏈'
    case 'BASEBALL': return '⚾'
    case 'BASKETBALL': return '🏀'
    case 'ICE_HOCKEY': return '🏒'
    case 'MMA': return '🥊'
    case 'TENNIS': return '🎾'
    default: return '🏆'
  }
}

// SportsbookSportIdentity.symbol(for:) (EventCardPresentation.swift)
export function sportSymbol(identifier) {
  switch (canonicalSportID(identifier)) {
    case 'FOOTBALL': return 'soccerball'
    case 'AMERICAN_FOOTBALL': return 'football.fill'
    case 'BASEBALL': return 'baseball.fill'
    case 'BASKETBALL': return 'basketball.fill'
    case 'ICE_HOCKEY': return 'hockey.puck.fill'
    case 'MMA': return 'figure.boxing'
    case 'TENNIS': return 'tennisball.fill'
    default: return 'trophy.fill'
  }
}

export function eventMatchesSelectedSport(event, selectedSportID) {
  return selectedSportID === ALL_SPORTS_ID || canonicalSportID(event.sport) === canonicalSportID(selectedSportID)
}

export function eventMatchesManifestScope(event, scope) {
  const normalizedScope = normalizedProviderID(scope)
  if (normalizedProviderID(event.sport) === normalizedScope || normalizedProviderID(event.league) === normalizedScope) return true
  if (LEAGUE_SCOPED_IDS.has(normalizedScope)) return false
  return canonicalSportID(event.sport) === canonicalSportID(normalizedScope)
}

// eventMatchesManifestFilter (Views.swift)
export function eventMatchesManifestFilter(event, { eventIds = [], sportIds = [] } = {}, selectedSportID = ALL_SPORTS_ID, quickFilter = '') {
  const explicitlyAllowed = eventIds.includes(event.id)
  const sportAllowed = sportIds.some((scope) => eventMatchesManifestScope(event, scope))
  if (!explicitlyAllowed && !sportAllowed) return false
  if (!eventMatchesSelectedSport(event, selectedSportID)) return false
  return eventMatchesQuickFilter(event, quickFilter)
}

// SportNavigationBar.catalogSports (AppChrome.swift)
export function catalogSports(events) {
  const counts = new Map()
  for (const event of events) {
    const id = canonicalSportID(event.sport)
    counts.set(id, (counts.get(id) || 0) + 1)
  }
  const identifiers = [...counts.keys()].sort((left, right) => {
    const leftIndex = SPORT_ORDER.indexOf(left) === -1 ? SPORT_ORDER.length : SPORT_ORDER.indexOf(left)
    const rightIndex = SPORT_ORDER.indexOf(right) === -1 ? SPORT_ORDER.length : SPORT_ORDER.indexOf(right)
    if (leftIndex !== rightIndex) return leftIndex - rightIndex
    return sportDisplayName(left).localeCompare(sportDisplayName(right), undefined, { sensitivity: 'base' })
  })
  return [
    { id: ALL_SPORTS_ID, name: 'All Sports', symbol: 'square.grid.2x2.fill', liveCount: events.length },
    ...identifiers.map((id) => ({ id, name: sportDisplayName(id), symbol: sportSymbol(id), liveCount: counts.get(id) || 0 })),
  ]
}

// makeSelectedSportEventSection (Views.swift)
export function selectedSportEventSection(components, events, selectedSportID, quickFilter = '') {
  if (selectedSportID === ALL_SPORTS_ID) return null
  const lists = components.filter((component) => component.type === 'event_list')
  const anchor = lists[0]
  if (!anchor) return null
  const included = new Set()
  const cardConfigurations = new Map()
  const matchingEvents = events.filter((event) => {
    if (!eventMatchesSelectedSport(event, selectedSportID)) return false
    if (!eventMatchesQuickFilter(event, quickFilter)) return false
    const source = lists.find((list) => eventMatchesManifestFilter(event, { eventIds: list.event_ids, sportIds: list.sport_ids }, selectedSportID, quickFilter))
    if (!source) return false
    if (included.has(event.id)) return false
    included.add(event.id)
    cardConfigurations.set(event.id, source)
    return true
  })
  if (matchingEvents.length === 0) return null
  return { anchorComponentID: anchor.id, title: `${sportDisplayName(selectedSportID)} Games`, events: matchingEvents, cardConfigurations }
}

// Odds / Line (BettingValues.swift): integer maths, truncating toward zero.
const ODDS_SCALE = 1000

export function americanOdds(millis) {
  if (millis === null || millis === undefined) return '—'
  const value = millis >= 2 * ODDS_SCALE
    ? Math.trunc(((millis - ODDS_SCALE) * 100) / ODDS_SCALE)
    : Math.trunc((-100 * ODDS_SCALE) / (millis - ODDS_SCALE))
  return value > 0 ? `+${value}` : `${value}`
}

export function fractionDigits(thousandths) {
  let remainder = thousandths
  if (remainder === 0) return ''
  let scale = 100
  let digits = ''
  while (remainder !== 0) {
    digits += String(Math.trunc(remainder / scale))
    remainder %= scale
    scale /= 10
  }
  return digits
}

export function lineDisplay(millis) {
  const whole = Math.trunc(millis / 1000)
  const remainder = Math.abs(millis % 1000)
  if (remainder === 0) return whole > 0 ? `+${whole}` : `${whole}`
  const sign = millis > 0 ? '+' : millis < 0 ? '-' : ''
  return `${sign}${Math.abs(whole)}.${fractionDigits(remainder)}`
}

// OddsSelectionFormatter (Widgets.swift)
const TOTAL_MARKETS = new Set(['TOTAL', 'TOTALS', 'OVER_UNDER'])
const MONEYLINE_MARKETS = new Set(['MONEYLINE', 'MONEY'])

export function sideToken(label) {
  const upper = label.toUpperCase()
  if (upper.startsWith('O')) return 'O'
  if (upper.startsWith('U')) return 'U'
  return label.slice(0, 1).toUpperCase()
}

export function unsignedHandicap(millis) {
  const magnitude = Math.abs(millis)
  const whole = Math.trunc(magnitude / 1000)
  const fraction = fractionDigits(magnitude % 1000)
  return fraction ? `${whole}.${fraction}` : `${whole}`
}

export function selectionText(outcome, marketType) {
  if (outcome.line === null || outcome.line === undefined) return outcome.label
  if (!TOTAL_MARKETS.has(marketType.toUpperCase())) return lineDisplay(outcome.line)
  return `${sideToken(outcome.label)} ${unsignedHandicap(outcome.line)}`
}

export function oddsCaption(outcome, marketType, context = 'standalone') {
  const market = marketType.toUpperCase()
  if (context === 'grid') {
    return MONEYLINE_MARKETS.has(market) ? null : selectionText(outcome, marketType)
  }
  if (!TOTAL_MARKETS.has(market) && outcome.line !== null && outcome.line !== undefined && outcome.label) return outcome.label
  return selectionText(outcome, marketType)
}

// EventCardMarketRole + eventCardMarketColumns (Models.swift)
const ROLES = {
  moneyline: { captionType: 'MONEYLINE', teamAligned: true, matches: (type, label) => type === 'MONEYLINE' || label.includes('MONEYLINE') || label.includes('MATCH RESULT') },
  spread: { captionType: 'SPREAD', teamAligned: true, matches: (type, label) => type === 'SPREAD' || label.includes('SPREAD') || label.includes('HANDICAP') },
  total: { captionType: 'TOTAL', teamAligned: false, matches: (type, label) => type === 'TOTAL' || label.includes('TOTAL') || label.includes('OVER/UNDER') },
  runLine: { captionType: 'SPREAD', teamAligned: true, matches: (type, label) => label.includes('RUN LINE') || type === 'SPREAD' },
  puckLine: { captionType: 'SPREAD', teamAligned: true, matches: (type, label) => label.includes('PUCK LINE') || type === 'SPREAD' },
  match: { captionType: 'MONEYLINE', teamAligned: true, matches: (type, label) => type === 'MONEYLINE' || label.includes('MONEYLINE') || label.includes('MATCH') || label.includes('WINNER') || label.includes('BOUT ODDS') },
}

function roleMatches(role, market) {
  return ROLES[role].matches(market.type.toUpperCase(), market.label.toUpperCase())
}

export function eventCardSport(event) {
  switch (canonicalSportID(event.sport)) {
    case 'AMERICAN_FOOTBALL': return 'americanFootball'
    case 'FOOTBALL': return 'soccer'
    case 'BASEBALL': return 'baseball'
    case 'BASKETBALL': return 'basketball'
    case 'ICE_HOCKEY': return 'hockey'
    case 'RUGBY': return 'rugby'
    case 'TENNIS': return 'tennis'
    default: return 'other'
  }
}

const COLUMN_SPECS = {
  americanFootball: [['SPREAD', 'spread'], ['MONEY', 'moneyline'], ['TOTAL', 'total']],
  basketball: [['SPREAD', 'spread'], ['MONEY', 'moneyline'], ['TOTAL', 'total']],
  baseball: [['MONEY', 'moneyline'], ['RUN', 'runLine'], ['TOTAL', 'total']],
  hockey: [['MONEY', 'moneyline'], ['PUCK', 'puckLine'], ['TOTAL', 'total']],
  rugby: [['MONEY', 'moneyline'], ['SPREAD', 'spread'], ['TOTAL', 'total']],
  tennis: [['MATCH', 'match'], ['SPREAD', 'spread'], ['TOTAL', 'total']],
  other: [['MONEY', 'moneyline'], ['SPREAD', 'spread'], ['TOTAL', 'total']],
}

export function competitorNames(event) {
  const away = event.participants?.find((participant) => participant.role === 'away')
  const home = event.participants?.find((participant) => participant.role === 'home')
  if (away && home) return [away.name, home.name]
  for (const separator of [' @ ', ' at ', ' vs ', ' - ']) {
    const index = event.name.toLowerCase().indexOf(separator.toLowerCase())
    if (index !== -1) return [event.name.slice(0, index), event.name.slice(index + separator.length)]
  }
  return [event.name, event.name]
}

function orderedOutcomes(event, market, role) {
  if (!ROLES[role].teamAligned) return market.outcomes.slice(0, 2)
  const used = new Set()
  const matched = competitorNames(event).map((name) => {
    if (!name) return null
    const outcome = market.outcomes.find((candidate) => !used.has(candidate.id) && candidate.label.toLowerCase().includes(name.toLowerCase()))
    if (outcome) used.add(outcome.id)
    return outcome || null
  }).filter(Boolean)
  return matched.length === 2 ? matched : market.outcomes.slice(0, 2)
}

function fallbackTitle(label) {
  const word = label.split(' ')[0]
  return word ? word.slice(0, 6).toUpperCase() : 'MARKET'
}

function column(title, market, outcomes, captionType) {
  return { id: `${market.id}:${title}`, title, market, outcomes, captionType }
}

function soccerColumns(event) {
  const resultMarket = event.markets.find((market) => {
    const label = market.label.toUpperCase()
    return market.outcomes.length >= 3 && (
      market.type.toUpperCase() === 'MONEYLINE' || label.includes('REGULAR TIME') || label.includes('MATCH RESULT') || label.includes('MONEYLINE') || label.includes('1X2')
    )
  })
  const columns = []
  if (resultMarket) {
    const canonical = ['1', 'X', '2'].map((label) => resultMarket.outcomes.find((outcome) => outcome.label.toUpperCase() === label)).filter(Boolean)
    const outcomes = canonical.length === 3 ? canonical : resultMarket.outcomes.slice(0, 3)
    columns.push(...outcomes.map((outcome) => column(outcome.label.toUpperCase(), resultMarket, [outcome], 'MONEYLINE')))
  }
  const totalMarket = event.markets.find((market) => roleMatches('total', market))
  if (totalMarket) columns.push(column('TOTAL', totalMarket, orderedOutcomes(event, totalMarket, 'total'), 'TOTAL'))
  if (columns.length === 0 && event.markets[0]) {
    const market = event.markets[0]
    return [column(fallbackTitle(market.label), market, market.outcomes.slice(0, 2), market.type)]
  }
  return columns
}

function defaultEventCardMarketColumns(event) {
  const sport = eventCardSport(event)
  if (sport === 'soccer') return soccerColumns(event)
  const used = new Set()
  const columns = []
  for (const [title, role] of COLUMN_SPECS[sport]) {
    const market = event.markets.find((candidate) => !used.has(candidate.id) && roleMatches(role, candidate))
    if (!market) continue
    used.add(market.id)
    columns.push(column(title, market, orderedOutcomes(event, market, role), ROLES[role].captionType))
  }
  if (columns.length === 0 && event.markets[0]) {
    const market = event.markets[0]
    return [column(fallbackTitle(market.label), market, market.outcomes.slice(0, 2), market.type)]
  }
  return columns
}

export function eventCardMarketColumns(event, configuration = {}) {
  const groups = new Map()
  for (const column of defaultEventCardMarketColumns(event)) {
    const group = groups.get(column.market.id) || []
    group.push(column)
    groups.set(column.market.id, group)
  }
  const ordered = [...groups.values()]
  const priority = configuration.market_priority || 'sport_default'
  const preferred = (group) => group[0].captionType.toLowerCase() === priority
  const result = priority === 'sport_default' ? ordered : [...ordered.filter(preferred), ...ordered.filter((group) => !preferred(group))]
  // Keep a three-way result together when limiting the number of markets.
  return result.slice(0, configuration.market_count ?? 3).flat()
}

// Catalog.applying(realtime) (Models.swift) — the fixture subset the simulator applies at launch.
export function applyFixtureRealtime(catalog, events = []) {
  const outcomes = new Map()
  const markets = new Map()
  const eventPatches = new Map()
  for (const event of events) {
    if (event.operation !== 'upsert' || !event.payload) continue
    if (event.entity_type === 'outcome') outcomes.set(event.entity_id, event.payload)
    if (event.entity_type === 'market') markets.set(event.entity_id, event.payload)
    if (event.entity_type === 'event') eventPatches.set(event.entity_id, event.payload)
  }
  return {
    ...catalog,
    events: catalog.events.map((event) => ({
      ...event,
      ...(eventPatches.get(event.id) || {}),
      markets: event.markets.map((market) => ({
        ...market,
        status: markets.get(market.id)?.status ?? market.status,
        outcomes: market.outcomes.map((outcome) => {
          const patch = outcomes.get(outcome.id)
          return patch ? { ...outcome, odds: patch.odds ?? outcome.odds, status: patch.status ?? outcome.status } : outcome
        }),
      })),
    })),
  }
}

export const PREVIEW_CATALOG = { ...applyFixtureRealtime(previewCatalogBundle, previewRealtimeBundle), freshness: 'fixture' }

export function firstLiveEvent(catalog = PREVIEW_CATALOG, quickFilter = '') {
  return catalog.events.find((event) => event.live && eventMatchesQuickFilter(event, quickFilter)) || null
}

// live_game_momentum event_selector semantics from the component registry.
export function liveGameEventCandidates(component, catalog = PREVIEW_CATALOG) {
  const selector = component.event_selector || {}
  const live = catalog.events.filter((event) => event.live)
  const scoped = live.filter((event) => (
    !selector.sport_ids?.length || selector.sport_ids.some((scope) => eventMatchesManifestScope(event, scope))
  ))
  const manual = selector.mode === 'manual' ? live.filter((event) => event.id === selector.event_id) : []
  const candidates = component.unavailable_behavior === 'first_live'
    ? [...manual, ...scoped, ...live]
    : selector.mode === 'first_live' ? scoped : manual
  const unique = [...new Set(candidates)]
  if (unique.length > 0) return unique
  // The runtime inventory has no in-play game, which the app answers by
  // applying unavailable_behavior. A section set to hide stays hidden; a
  // section that would show the next matching live game rehearses the game
  // it will show first: the chosen one when it is in the catalog, otherwise
  // the soonest upcoming game inside its own scope. The fixture keeps its
  // live game, so this never applies to it.
  if (component.unavailable_behavior !== 'first_live' || catalog.freshness === 'fixture' || !catalog.freshness) return []
  const chosen = selector.mode === 'manual' ? catalog.events.find((event) => event.id === selector.event_id) : null
  const upcoming = chosen ? [chosen] : catalog.events
    .filter((event) => !event.live && (!selector.sport_ids?.length || selector.sport_ids.some((scope) => eventMatchesManifestScope(event, scope))))
    .sort((a, b) => new Date(a.start_time || 0) - new Date(b.start_time || 0))
  return upcoming.length ? [{ ...upcoming[0], upcoming: true }] : []
}

export function resolveLiveGameEvent(component, catalog = PREVIEW_CATALOG) {
  return liveGameEventCandidates(component, catalog)[0] || null
}

export function compareVersions(left, right) {
  const parse = (value) => String(value ?? '0').split('.').map((part) => Number.parseInt(part, 10) || 0)
  const [a, b] = [parse(left), parse(right)]
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const difference = (a[index] || 0) - (b[index] || 0)
    if (difference !== 0) return difference < 0 ? -1 : 1
  }
  return 0
}


// Text(event.startTime.formatted(date: .abbreviated, time: .shortened))
export function formatStartTime(iso, locale = 'en-US', timeZone) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const day = date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone })
  const time = date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', timeZone })
  return `${day} at ${time}`
}

// MatchClock.display
export function clockDisplay(clock) {
  if (!clock) return ''
  const seconds = String(clock.second ?? 0).padStart(2, '0')
  return clock.period ? `${clock.period} ${clock.minute}:${seconds}` : `${clock.minute}:${seconds}`
}

// LiveShowcaseCard.homeMomentum
export function homeMomentum(score) {
  if (!score) return 0.5
  const home = Number(score.home_score)
  const away = Number(score.away_score)
  if (!Number.isFinite(home) || !Number.isFinite(away) || home + away <= 0) return 0.5
  return Math.min(Math.max(home / (home + away), 0.05), 0.95)
}

// AppTheme.from(tokens:) (ThemeResolution.swift) + the onAccent luminance rule (docs/design-system.md §1.3)
function channel(value) {
  const linear = value / 255
  return linear <= 0.03928 ? linear / 12.92 : ((linear + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(hex) {
  const cleaned = String(hex).replace(NON_HEX_CHARACTERS_PATTERN, '')
  const expanded = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned.slice(0, 6)
  if (expanded.length !== 6) return 0
  const value = Number.parseInt(expanded, 16)
  return 0.2126 * channel(value >> 16) + 0.7152 * channel((value >> 8) & 0xff) + 0.0722 * channel(value & 0xff)
}

// 0.179 is where black and white reach equal contrast against a fill, so whichever side the
// luminance falls on is the higher-contrast foreground (AppTheme.legibleForeground on iOS).
const FOREGROUND_CROSSOVER_LUMINANCE = 0.179

export function onAccentColor(accent) {
  return relativeLuminance(accent) >= FOREGROUND_CROSSOVER_LUMINANCE ? '#000000' : '#FFFFFF'
}

export function onHighlightColor(highlight) {
  return relativeLuminance(highlight) >= FOREGROUND_CROSSOVER_LUMINANCE ? '#16121C' : '#FFFFFF'
}

export function supportedAppearances(theme) {
  if (!theme?.appearance?.schemes) return ['dark']
  return ['light', 'dark'].filter((key) => theme.appearance.schemes[key] !== undefined)
}

export function resolveNativeTheme(theme, requestedAppearance) {
  const supported = supportedAppearances(theme)
  const defaultAppearance = supported.includes(theme?.appearance?.default) ? theme.appearance.default : supported[0]
  const appearance = supported.includes(requestedAppearance) ? requestedAppearance : defaultAppearance
  const colors = { ...(theme?.colors || {}), ...(theme?.appearance?.schemes?.[appearance] || {}) }
  const accent = colors.accent || '#FFB800'
  const brandSecondary = colors.brand_secondary || accent
  const light = appearance === 'light'
  const highlight = colors.highlight || (light ? '#8A6100' : '#FFD166')
  return {
    name: theme?.name || 'Sportsbook',
    appearance,
    supportsAppearanceToggle: supported.length > 1,
    background: colors.background || '#080A0F',
    backgroundElevated: colors.background_elevated || '#0E121B',
    surface: colors.surface || '#121620',
    surfaceSubtle: colors.surface_subtle || '#181E2C',
    surfaceInteractive: colors.surface_interactive || '#222A3D',
    textPrimary: colors.text_primary || '#FFFFFF',
    textSecondary: colors.text_secondary || '#94A3B8',
    textTertiary: colors.text_tertiary || '#64748B',
    accent,
    brandSecondary,
    onAccent: onAccentColor(accent),
    highlight,
    onHighlight: onHighlightColor(highlight),
    positive: colors.positive || '#10B981',
    negative: colors.negative || '#EF4444',
    warning: colors.warning || '#F59E0B',
    borderSubtle: colors.border_subtle || '#63358B',
    cardRadius: theme?.radius?.lg ?? 16,
    buttonRadius: theme?.radius?.md ?? 12,
  }
}

export const resolvePreviewTheme = resolveNativeTheme

export function previewThemeVariables(resolved) {
  return {
    '--np-bg': resolved.background,
    '--np-bg-elevated': resolved.backgroundElevated,
    '--np-surface': resolved.surface,
    '--np-surface-subtle': resolved.surfaceSubtle,
    '--np-surface-interactive': resolved.surfaceInteractive,
    '--np-text': resolved.textPrimary,
    '--np-text-secondary': resolved.textSecondary,
    '--np-text-tertiary': resolved.textTertiary,
    '--np-accent': resolved.accent,
    '--np-brand-secondary': resolved.brandSecondary,
    '--np-on-accent': resolved.onAccent,
    '--np-highlight': resolved.highlight,
    '--np-on-highlight': resolved.onHighlight,
    '--np-positive': resolved.positive,
    '--np-negative': resolved.negative,
    '--np-warning': resolved.warning,
    '--np-border': resolved.borderSubtle,
    '--np-border-subtle': resolved.borderSubtle,
    // AppTheme.from (ThemeResolution.swift): borderActive = accent,
    // accentGlow = brandSecondary.opacity(0.30), surfaceInteractiveActive = accent.opacity(0.2).
    '--np-border-active': resolved.accent,
    '--np-accent-glow': `color-mix(in srgb, ${resolved.brandSecondary} 30%, transparent)`,
    '--np-surface-interactive-active': `color-mix(in srgb, ${resolved.accent} 20%, transparent)`,
    '--np-card-radius': `${resolved.cardRadius}px`,
    '--np-button-radius': `${resolved.buttonRadius}px`,
  }
}

// AppTypography (DesignSystem.swift) and WagerTypography.kt: the theme names a
// family per role. Rounded roles are SF Pro Rounded on iPhone and the bundled
// Nunito on Android; numbers are SF Mono or the bundled Geist Mono.
const FONT_STACKS = {
  ios: {
    rounded: 'ui-rounded, "SF Pro Rounded", "Wager Nunito", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    system: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
    mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  },
  android: {
    rounded: '"Wager Nunito", Roboto, system-ui, sans-serif',
    system: 'Roboto, system-ui, sans-serif',
    mono: '"Geist Mono", ui-monospace, monospace',
  },
}

export function previewFontVariables(typography, device) {
  const stacks = FONT_STACKS[device] ?? FONT_STACKS.ios
  const display = ['system_rounded', 'brand_display'].includes(typography?.display_family ?? 'system_rounded')
  const body = ['system_rounded', 'brand_body'].includes(typography?.body_family ?? 'system')
  return {
    '--np-font-display': display ? stacks.rounded : stacks.system,
    '--np-font-body': body ? stacks.rounded : stacks.system,
    '--np-font-mono': typography?.number_family === 'brand_numbers' ? stacks.rounded : stacks.mono,
  }
}

// AppHeaderView.brandMonogram
export function brandMonogram(name) {
  if (!name) return 'SB'
  const first = name[0]
  const rest = name.slice(1)
  const uppercase = [...rest].find((character) => character !== character.toLowerCase() && character === character.toUpperCase())
  return `${first}${uppercase ?? rest[0] ?? first}`
}

// QuickFilterBar.filters (LiveWidgets.swift) — mirrors the iOS quick filters bar.
export const QUICK_FILTERS = [
  { id: 'Featured', label: 'Featured', symbol: 'sparkles' },
  { id: 'In-Play Live', label: 'In-Play Live', symbol: 'bolt.fill' },
  { id: 'Same Game Parlays', label: 'Same Game Parlays', symbol: 'square.stack.3d.up.fill' },
  { id: 'Football', label: 'Football', symbol: 'football.fill' },
  { id: 'Baseball', label: 'Baseball', symbol: 'baseball.fill' },
  { id: 'Soccer', label: 'Soccer', symbol: 'soccerball' },
]

// HomeView.matchesQuickFilter (HomeView.swift); `filter` is a QUICK_FILTERS id.
export function eventMatchesQuickFilter(event, filter = '') {
  switch (filter) {
    case 'In-Play Live': return Boolean(event.live)
    case 'Same Game Parlays': return (event.markets || []).some((market) => market.metadata?.tags?.includes('BET_BUILDER'))
    case 'Football': return canonicalSportID(event.sport) === 'AMERICAN_FOOTBALL'
    case 'Baseball': return canonicalSportID(event.sport) === 'BASEBALL'
    case 'Soccer': return canonicalSportID(event.sport) === 'FOOTBALL'
    default: return true
  }
}

// PayoutCalculator (BettingValues.swift): combined parlay price in milliformat.
export function combinedOddsMillis(oddsList) {
  let combined = ODDS_SCALE
  for (const odds of oddsList) {
    if (odds === null || odds === undefined) return null
    combined = Math.trunc((combined * odds) / ODDS_SCALE)
  }
  return combined
}

// American price typed by an operator -> milliformat used on the wire.
export function americanToMillis(american) {
  const value = Number.parseInt(String(american).replace(NON_SIGNED_DIGIT_PATTERN, ''), 10)
  if (!Number.isFinite(value) || value === 0) return null
  if (value > 0) return ODDS_SCALE + value * 10
  return ODDS_SCALE + Math.trunc((100 * ODDS_SCALE) / Math.abs(value))
}

export function outcomeReference(catalog, reference) {
  if (!reference?.event_id) return null
  const event = catalog.events.find((candidate) => candidate.id === reference.event_id)
  const market = event?.markets.find((candidate) => candidate.id === reference.market_id)
  const outcome = market?.outcomes.find((candidate) => candidate.id === reference.outcome_id)
  if (!event || !market || !outcome) return null
  return { event, market, outcome }
}

// Markets an operator can author a boost or leg against right now: open, with
// open outcomes that carry a price. Suspended or closed inventory is excluded so
// a pick never silently lands on something customers cannot bet.
const isOpen = (status) => !status || status === 'OPEN'
export function bettableMarkets(event) {
  return (event?.markets || [])
    .filter((market) => isOpen(market.status))
    .map((market) => ({ ...market, outcomes: (market.outcomes || []).filter((outcome) => isOpen(outcome.status) && outcome.odds !== null && outcome.odds !== undefined) }))
    .filter((market) => market.outcomes.length)
}

// Every leg must resolve; a parlay with a missing leg is a different bet, so the
// app skips the card rather than quietly shortening it.
export function parlayLegs(card, catalog = PREVIEW_CATALOG) {
  const legs = (card.legs || []).map((leg) => outcomeReference(catalog, leg))
  return legs.every(Boolean) ? legs : []
}

// featured_showcase event_spotlight selector: manual, first live, or next to start.
export function spotlightEventCandidates(card, catalog = PREVIEW_CATALOG, now = new Date()) {
  const selector = card.event_selector || {}
  const inScope = (event) => !selector.sport_ids?.length || selector.sport_ids.some((scope) => eventMatchesManifestScope(event, scope))
  if (selector.mode === 'manual') {
    return catalog.events.filter((event) => event.id === selector.event_id)
  }
  if (selector.mode === 'first_live') {
    return catalog.events.filter((event) => event.live && inScope(event))
  }
  // next_up: the earliest event in scope that has not started; a game that has
  // already started but is not live is stale, so the card is skipped instead.
  return catalog.events
    .filter((event) => !event.live && inScope(event) && new Date(event.start_time) >= now)
    .sort((left, right) => new Date(left.start_time) - new Date(right.start_time))
}

export function resolveSpotlightEvent(card, catalog = PREVIEW_CATALOG, now = new Date()) {
  return spotlightEventCandidates(card, catalog, now)[0] || null
}

// Card schedules are absolute windows; the app filters by wall-clock time.
export function cardIsActive(card, now = new Date()) {
  const starts = card.schedule?.starts_at ? new Date(card.schedule.starts_at) : null
  const ends = card.schedule?.ends_at ? new Date(card.schedule.ends_at) : null
  if (starts && !Number.isNaN(starts.getTime()) && now < starts) return false
  if (ends && !Number.isNaN(ends.getTime()) && now >= ends) return false
  return true
}

export function cardScheduleState(card, now = new Date()) {
  if (cardIsActive(card, now)) return 'active'
  const starts = card.schedule?.starts_at ? new Date(card.schedule.starts_at) : null
  return starts && now < starts ? 'scheduled' : 'expired'
}

export function activeCards(component, now = new Date()) {
  return (component.cards || []).filter((card) => cardIsActive(card, now))
}
